import { useState, useCallback, useEffect } from 'react';
import { authApi } from '@/api/auth';
import apiClient from '@/api/client';
import type { AuthUser } from '@/types';

function getStoredUser(): AuthUser | null {
  const raw = localStorage.getItem('authUser');
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(getStoredUser);
  const [loading, setLoading] = useState(() => !!getStoredUser());

  // On mount, verify the cookie is still valid by calling /auth/me
  useEffect(() => {
    const stored = getStoredUser();
    if (!stored) {
      setLoading(false);
      return;
    }

    authApi
      .me()
      .then((res) => {
        const apiUser = res.data;
        const verified: AuthUser = {
          id: apiUser.id,
          role: apiUser.role as AuthUser['role'],
          username: apiUser.username,
          name: apiUser.name ?? null,
          phone: apiUser.phone ?? null,
          avatarUrl: apiUser.avatarUrl ?? null,
          createdAt: apiUser.createdAt ?? '',
        };
        localStorage.setItem('authUser', JSON.stringify(verified));
        setUser(verified);
      })
      .catch(() => {
        // Cookie expired, invalidated, or network error — treat as unauthenticated
        localStorage.removeItem('authUser');
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const response = await authApi.login(username, password);
    const { user: apiUser } = response.data;
    const authUser: AuthUser = {
      id: apiUser.id,
      role: apiUser.role as AuthUser['role'],
      username: apiUser.username,
      name: apiUser.name ?? null,
      phone: apiUser.phone ?? null,
      avatarUrl: apiUser.avatarUrl ?? null,
      createdAt: apiUser.createdAt ?? '',
    };
    localStorage.setItem('authUser', JSON.stringify(authUser));
    setUser(authUser);
    return { user: authUser, requirePasswordChange: !!apiUser.requirePasswordChange };
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch {
      // Best-effort — clear local state regardless
    }
    localStorage.removeItem('authUser');
    setUser(null);
    window.location.href = '/login';
  }, []);

  const setUserFromPortal = useCallback((portalUser: AuthUser) => {
    localStorage.setItem('authUser', JSON.stringify(portalUser));
    setUser(portalUser);
  }, []);

  return { isAuthenticated: !!user, loading, user, login, logout, setUserFromPortal };
}
